/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * Country name → ISO 3166 alpha-2, for registry calls (Kyckr keys jurisdictions
 * by ISO code). Names are matched through canonicalPlace so the same
 * normalisation that serves the risk engine serves the registry. Deliberately
 * NOT exhaustive: it covers the jurisdictions this client base actually
 * presents plus Kyckr's strongest coverage, and an unmapped name returns null
 * so callers fall back to asking the officer rather than guessing a code.
 */

import { canonicalPlace } from './country-canonical.js';

const MAP = {
  'united kingdom': 'GB', 'uk': 'GB', 'great britain': 'GB', 'england': 'GB', 'scotland': 'GB', 'wales': 'GB', 'northern ireland': 'GB',
  'ireland': 'IE', 'republic of ireland': 'IE',
  'switzerland': 'CH', 'germany': 'DE', 'france': 'FR', 'netherlands': 'NL', 'belgium': 'BE', 'luxembourg': 'LU',
  'austria': 'AT', 'italy': 'IT', 'spain': 'ES', 'portugal': 'PT', 'denmark': 'DK', 'sweden': 'SE', 'norway': 'NO',
  'finland': 'FI', 'iceland': 'IS', 'estonia': 'EE', 'latvia': 'LV', 'lithuania': 'LT', 'poland': 'PL',
  'czech republic': 'CZ', 'czechia': 'CZ', 'slovakia': 'SK', 'hungary': 'HU', 'romania': 'RO', 'bulgaria': 'BG',
  'greece': 'GR', 'cyprus': 'CY', 'malta': 'MT', 'croatia': 'HR', 'slovenia': 'SI',
  'gibraltar': 'GI', 'guernsey': 'GG', 'jersey': 'JE', 'isle of man': 'IM',
  'united states': 'US', 'usa': 'US', 'united states of america': 'US', 'canada': 'CA',
  'australia': 'AU', 'new zealand': 'NZ',
  'hong kong': 'HK', 'singapore': 'SG', 'japan': 'JP', 'china': 'CN', 'malaysia': 'MY',
  'united arab emirates': 'AE', 'uae': 'AE', 'dubai': 'AE',
  'brazil': 'BR', 'south africa': 'ZA', 'india': 'IN', 'israel': 'IL', 'turkey': 'TR',
  'liechtenstein': 'LI', 'monaco': 'MC',
  'british virgin islands': 'VG', 'bvi': 'VG', 'cayman islands': 'KY', 'bermuda': 'BM',
  'bahamas': 'BS', 'barbados': 'BB', 'mauritius': 'MU', 'seychelles': 'SC',
  'saint vincent and the grenadines': 'VC', 'st vincent and the grenadines': 'VC', 'st. vincent and the grenadines': 'VC',
  'marshall islands': 'MH', 'belize': 'BZ', 'panama': 'PA', 'costa rica': 'CR',
  'georgia': 'GE', 'ukraine': 'UA', 'serbia': 'RS'
};

/** "United Kingdom" → 'GB'; unknown → null (never guess). */
export function isoFromCountryName(name) {
  if (!name) return null;
  const direct = String(name).trim();
  if (/^[A-Za-z]{2}$/.test(direct)) return direct.toUpperCase();
  const canon = String(canonicalPlace(direct) || direct).toLowerCase().trim();
  return MAP[canon] || MAP[direct.toLowerCase().trim()] || null;
}

export default isoFromCountryName;
